import styled from '@emotion/styled';
import { Layout, message, Skeleton } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { IconCreateBlack } from '../../../assets';
import { PER_PAGE_DEFAULT } from '../../../constants/common';
import { MISC } from '../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../constants/message';
import { API_URLS } from '../../../constants/urls/api-urls';
import { getExternalToolRepository } from '../../../repository/mytm/external-tool/externalToolRepository';
import { appStyles } from '../../../styles';
import { SpinLoading } from '../../atoms';
import { ContentPageHeader } from '../../molecules/page-header/ContentPageHeader';
import { ExternalToolsModal, ExternalToolsTable } from '../../organisms/misc';

const { Content } = Layout;

export const ExternalTools = () => {
  const { t } = useTranslation(MISC);
  const [page] = useState(1);
  const [perPage] = useState(PER_PAGE_DEFAULT);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState();
  const [isLoadingSpin, setIsLoadingSpin] = useState(false);
  const errorCount = useRef(0);

  const { data, error, mutate, isValidating } = useSWR(
    [API_URLS.EXTERNAL_TOOLS, page, perPage],
    (url: string, pageNumber: number, take) =>
      getExternalToolRepository({
        page: pageNumber,
        take,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const onOpenModal = useCallback(() => {
    setIsOpenModal(true);
  }, []);

  const onSetCurrentRecord = useCallback(record => {
    setCurrentRecord(record);
  }, []);

  const onSetLoading = useCallback((loading: boolean) => {
    setIsLoadingSpin(loading);
  }, []);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  if (!data || isValidating) {
    return <Skeleton />;
  }

  const records = data.data.map((record: any, index: number) => ({
    ...record,
    index,
  }));

  return (
    <>
      <Container>
        <Content>
          <ContentPageHeader
            title={t('title.external')}
            extra={[
              {
                key: 1,
                text: t('buttonCreate'),
                bgWhite: 'true',
                onClick: () => {
                  setIsOpenModal(true);
                  setCurrentRecord(undefined);
                },
                icon: <IconCreateBlack />,
              },
            ]}
          />
          <ExternalToolsTable
            records={records}
            onOpenModal={onOpenModal}
            setCurrentRecord={onSetCurrentRecord}
            onRefresh={mutate}
            onSetLoading={onSetLoading}
          />
          <SpinLoading isLoading={isLoadingSpin} absolute>
            <ExternalToolsModal
              isOpenModal={isOpenModal}
              onClose={onCloseModal}
              record={currentRecord}
              onRefresh={mutate}
              onSetLoading={onSetLoading}
              onSetCurrentRecord={onSetCurrentRecord}
            />
          </SpinLoading>
        </Content>
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .btn-generate-access-code,
  .btn-export-access-code {
    width: 140px;
  }
`;
